import type { MutationEntry } from './db';

/**
 * R2 H-5 — retry backoff.
 *
 * The outbox used to retry with no delay at all: `processMutation` set a failed
 * item straight back to `pending`, and `triggerSync` picked every `pending` row
 * up again immediately. A captive portal or a thirty-second 5xx burst therefore
 * consumed all five attempts in milliseconds and dropped the crew's work into
 * `failed` — a blip converted into permanent failure.
 *
 * The schedule is DERIVED, never stored: `lastAttempt` and `attempts` are both
 * already persisted on every mutation row, so the delay survives a reload with
 * no IndexedDB schema change. Reopening the app re-computes the same due time
 * rather than resetting the clock or firing everything at once.
 */

/** Attempts allowed before an item is genuinely failed. */
export const MAX_RETRY_ATTEMPTS = 5;

/** First retry waits this long; each subsequent one doubles. */
export const RETRY_BASE_MS = 15_000;

/** Ceiling on a single wait, so attempt five is not hours away. */
export const RETRY_MAX_DELAY_MS = 10 * 60_000;

/**
 * Stable 32-bit FNV-1a hash → a fraction in [0, 1).
 *
 * Jitter has to come from something PERSISTED, not from `Math.random()`: a
 * random draw would produce a different due time on every render and every
 * reload, which is exactly the reload-stability R2-5 asks for. Hashing the
 * item's own idempotency key gives each item its own fixed offset — so the
 * whole queue does not retry in lockstep the instant signal returns — while
 * recomputing to the same value forever.
 */
function jitterFraction(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10_000) / 10_000;
}

/**
 * How long to wait after `attempts` failed attempts, for the item owning `key`.
 * Exponential (15s, 30s, 1m, 2m, 4m …) capped at ten minutes, spread ±25% by
 * the item's stable jitter. Five attempts therefore span minutes, not
 * milliseconds.
 */
export function retryDelayMs(attempts: number, key: string): number {
  if (attempts < 1) return 0;
  const exponential = Math.min(RETRY_BASE_MS * 2 ** (attempts - 1), RETRY_MAX_DELAY_MS);
  return Math.round(exponential * (0.75 + jitterFraction(key) * 0.5));
}

/**
 * When this item may next be attempted, or `null` if it is due now.
 *
 * `null` covers the cases that must never wait: a brand-new item, and one whose
 * `lastAttempt` was never recorded (rows predating this change, or written by
 * an older build). Treating an unknown history as "due" keeps the outbox
 * draining rather than stranding work behind a schedule we cannot reconstruct.
 */
export function nextAttemptAt(m: Pick<MutationEntry, 'attempts' | 'lastAttempt' | 'idempotencyKey'>): Date | null {
  if (m.attempts < 1 || !m.lastAttempt) return null;
  const last = m.lastAttempt instanceof Date ? m.lastAttempt : new Date(m.lastAttempt);
  if (Number.isNaN(last.getTime())) return null;
  return new Date(last.getTime() + retryDelayMs(m.attempts, m.idempotencyKey));
}

/** Is this item allowed to be attempted yet? */
export function isDue(
  m: Pick<MutationEntry, 'attempts' | 'lastAttempt' | 'idempotencyKey'>,
  now: Date = new Date(),
): boolean {
  const due = nextAttemptAt(m);
  return due === null || due.getTime() <= now.getTime();
}

/**
 * Plain-words wait for the Sync Center — "in about 2 minutes". Deliberately
 * approximate: a crew needs to know it is minutes not hours, and a
 * to-the-second countdown would only invite watching it.
 */
export function describeWait(due: Date | null, now: Date = new Date()): string | null {
  if (!due) return null;
  const ms = due.getTime() - now.getTime();
  if (ms <= 0) return null;
  const minutes = Math.round(ms / 60_000);
  if (minutes >= 1) return `in about ${minutes} minute${minutes === 1 ? '' : 's'}`;
  return `in under a minute`;
}
